import React, { useEffect, useRef } from 'react';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';

interface SshTerminalViewportProps {
  sessionId: string;
  fontFamily: string;
  fontSize: number;
  textColor: string;
  onSendInput: (input: string) => void;
  onResize: (cols: number, rows: number) => void;
  onInterrupt: () => void;
  onWriterChange: (writer: ((chunk: string) => void) | null) => void;
  onError?: (error: unknown) => void;
}

const SshTerminalViewport: React.FC<SshTerminalViewportProps> = ({
  sessionId,
  fontFamily,
  fontSize,
  textColor,
  onSendInput,
  onResize,
  onInterrupt,
  onWriterChange,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let resizeObserver: ResizeObserver | null = null;
    let dataDisposable: { dispose: () => void } | null = null;

    try {
      const terminal = new XTerm({
        cursorBlink: true,
        convertEol: false,
        fontFamily,
        fontSize,
        theme: {
          background: '#000000',
          foreground: textColor,
          cursor: textColor,
        },
      });

      const fitAddon = new FitAddon();
      terminal.loadAddon(fitAddon);
      terminal.open(containerRef.current);
      fitAddon.fit();
      onResize(terminal.cols, terminal.rows);
      terminal.focus();

      terminalRef.current = terminal;
      fitAddonRef.current = fitAddon;

      dataDisposable = terminal.onData((data) => {
        onSendInput(data);
      });

      terminal.attachCustomKeyEventHandler((event) => {
        if (
          event.type === 'keydown'
          && event.ctrlKey
          && !event.shiftKey
          && !event.altKey
          && event.key.toLowerCase() === 'c'
          && !terminal.hasSelection()
        ) {
          event.preventDefault();
          onInterrupt();
          return false;
        }

        return true;
      });

      resizeObserver = new ResizeObserver(() => {
        fitAddon.fit();
        onResize(terminal.cols, terminal.rows);
      });
      resizeObserver.observe(containerRef.current);

      onWriterChange((chunk: string) => {
        terminal.write(chunk);
      });
    } catch (error) {
      onWriterChange(null);
      onError?.(error);
      return;
    }

    return () => {
      onWriterChange(null);
      resizeObserver?.disconnect();
      dataDisposable?.dispose();
      if (terminalRef.current) {
        terminalRef.current.dispose();
      }
      terminalRef.current = null;
      fitAddonRef.current = null;
    };
  }, [fontFamily, fontSize, onError, onInterrupt, onResize, onSendInput, onWriterChange, textColor]);

  useEffect(() => {
    const terminal = terminalRef.current;
    const fitAddon = fitAddonRef.current;
    if (!terminal || !fitAddon) {
      return;
    }

    try {
      terminal.options = {
        fontFamily,
        fontSize,
        theme: {
          background: '#000000',
          foreground: textColor,
          cursor: textColor,
        },
      };
      fitAddon.fit();
      onResize(terminal.cols, terminal.rows);
      terminal.focus();
    } catch (error) {
      onError?.(error);
    }
  }, [sessionId, fontFamily, fontSize, onError, onResize, textColor]);

  return <div ref={containerRef} className="h-full min-h-0 w-full overflow-hidden" />;
};

export default SshTerminalViewport;